import React, { useEffect, useState } from "react";
import {
  FaTag,
  FaArrowRight,
  FaGift,
  FaCar,
} from "react-icons/fa";

import { useNavigate } from "react-router-dom";

import api from "../api/Api.js";
import { useLanguage } from "../context/LanguageContext";

import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/css/Offer.css";

const DEFAULT_ERROR = "__default__";

const Offer = () => {
  const navigate = useNavigate();

  const { language, t } = useLanguage();

  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // =====================================================
  // GET LANGUAGE ID
  // =====================================================

  const getLanguageId = () => {
    if (language && typeof language === "object") {
      return (
        Number(language.lang_id || language.language_id || language.id) || 1
      );
    }

    return Number(language) || 1;
  };

  const languageId = getLanguageId();

  // =====================================================
  // FETCH OFFER CONTENT (page_id = 18)
  // =====================================================

  useEffect(() => {
    let cancelled = false;

    const fetchOfferContent = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await api.get("/contents/content", {
          params: {
            page_id: 18,
            lang_id: languageId,
          },
        });

        if (cancelled) return;

        if (response.data?.success && response.data?.data) {
          setContent(response.data.data);
        } else {
          setError(response.data?.message || DEFAULT_ERROR);
        }
      } catch (err) {
        if (cancelled) return;

        console.error("OFFER CONTENT ERROR:", err);

        setError(err.response?.data?.message || DEFAULT_ERROR);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchOfferContent();

    return () => {
      cancelled = true;
    };
  }, [languageId]);

  // =====================================================
  // NAVIGATION
  // =====================================================

  const goTo = (path) => {
    navigate(path);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div className="offer-page">
        <section className="offer-loading">
          <div className="offer-loader"></div>

          <h3>{t("offerpage.offerpage0", "Loading offers...")}</h3>

          <p>
            {t(
              "offerpage.offerpage1",
              "Please wait while we load our latest offers."
            )}
          </p>
        </section>
      </div>
    );
  }

  // =====================================================
  // ERROR
  // =====================================================

  if (error) {
    return (
      <div className="offer-page">
        <section className="offer-error-wrapper">
          <div className="offer-error-card">
            <div className="offer-error-icon">
              <FaTag />
            </div>

            <h2>{t("offerpage.offerpage2", "Offers unavailable")}</h2>

            <p>
              {error === DEFAULT_ERROR
                ? t(
                    "offerpage.offerpage5",
                    "Unable to load offers. Please try again."
                  )
                : error}
            </p>

            <button
              type="button"
              onClick={() => window.location.reload()}
              className="offer-primary-btn"
            >
              {t("offerpage.offerpage3", "Try Again")}
            </button>
          </div>
        </section>
      </div>
    );
  }

  // =====================================================
  // DATA
  // =====================================================

  const title =
    content?.cont_title || t("menu.menu14", "Special Offers");

  const details = content?.cont_details || "";

  // =====================================================
  // PAGE
  // =====================================================

  return (
    <div className="offer-page">
      {/* ================= HERO ================= */}
      <div className="bg-box">
        <section className="modify1 offer-hero">
          <div className="container">
            <div className="offer-hero-content">
              <div>
                <div className="offer-small-label">
                 {t("othersnewstatics.othersnewstatic35", "SPECIAL DEALS")}
                </div>

                <h1>{title}</h1>

                <p>
                {t("othersnewstatics.othersnewstatic36", 
                "Discover our latest car rental offers and enjoy more value on your journey.")}
                 
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ================= CONTENT ================= */}
      <main className="offer-main">
        <div className="container">
          <div className="offer-layout">
            {/* Main offer content */}
            <section className="offer-content-card">
              <div className="offer-card-top">
                <div className="offer-card-icon">
                  <FaGift />
                </div>

                <div>
                  <span className="offer-label">
                    {t("othersnewstatics.othersnewstatic37", "EXCLUSIVE OFFERS")}
                  </span>

                  <h2>{title}</h2>
                </div>
              </div>

              <div
                className="offer-rich-content"
                dangerouslySetInnerHTML={{
                  __html: details,
                }}
              />
            </section>

            {/* Side CTA */}
            <aside className="offer-sidebar">
              <div className="offer-book-card">
                <div className="offer-book-icon">
                  <FaCar />
                </div>

                <span className="offer-book-label">
                  {t("othersnewstatics.othersnewstatic38", "GREAT VALUE")}
                </span>

                <h3>
                 {t("othersnewstatics.othersnewstatic39", "Find your perfect car")}
                </h3>

                <p>
                  {t("othersnewstatics.othersnewstatic40", 
                  "Check our available cars and book your next journey at the best available rate")}
                </p>

                <button
                  type="button"
                  className="offer-book-btn"
                  onClick={() => goTo("/bookacar")}
                >
                  {t("menu.menu1", "Book a Car")}

                  <FaArrowRight />
                </button>
              </div>

              {/* Our Cars */}
              <button
                type="button"
                className="offer-cars-link"
                onClick={() => goTo("/ourcars")}
              >
                <div className="offer-cars-link-icon">
                  <FaCar />
                </div>

                <div className="offer-cars-link-text">
                  <strong>
                  {t("othersnewstatics.othersnewstatic41", "Explore Our Cars")}
                  </strong>

                  <span>
                  {t("othersnewstatics.othersnewstatic42", "View our fleet")}
                  </span>
                </div>

                <FaArrowRight />
              </button>
            </aside>
          </div>

          {/* ================= BOTTOM CTA ================= */}
          <section className="offer-bottom-cta">
            <div className="offer-bottom-icon">
              <FaTag />
            </div>

            <div className="offer-bottom-text">
              <h3>
              {t("othersnewstatics.othersnewstatic43", "Don't miss our special offers")}
              </h3>

              <p>
              {t("othersnewstatics.othersnewstatic44", " Choose your dates and find the right car for your trip.")}
              </p>
            </div>

            <button
              type="button"
              onClick={() => goTo("/ourcars")}
              className="offer-bottom-btn"
            >
               {t("othersnewstatics.othersnewstatic45", "View cars")}
              <FaArrowRight />
            </button>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Offer;